import {
  useTabsStore,
  guardDirtyDbTab,
  setDbManagerTabs,
  setTabStripOrder,
  setActiveDbManagerTabId,
  setActiveTabType,
} from './tabsStore';
import { appendTabStripEntry } from '../utils/workspaceTabPersistence';

/**
 * The database overview tab (DBeaver's database editor / DataGrip's schema
 * view): a `db-manager-table` tab with `view: 'overview'` and no table.
 *
 * Its own module so the 4,000-line results tab and the tab store only carry
 * call sites. `overviewMode` is which half is showing — 'objects' (the lists)
 * or 'diagram' (the ER diagram).
 */

export const OVERVIEW_VIEW = 'overview';

export function isDbOverviewTab(tab) {
  return !!tab && tab.view === OVERVIEW_VIEW;
}

/** One tab per (connection, database/schema): re-opening focuses it. */
export function dbOverviewTabKey({ extensionId, connId, dbName }) {
  return `db-manager-overview:${extensionId}:${connId}:${dbName ?? ''}`;
}

/** The engines the overview has catalog SQL for (dbOverviewSql.json). */
const OVERVIEW_ENGINES = new Set(['mysql', 'postgres', 'sqlite']);

export function openDbOverviewTab({ extensionId, connId, engine, dbName, label, connLabel, mode }) {
  if (!extensionId || typeof connId !== 'string' || !connId || !OVERVIEW_ENGINES.has(engine)) return null;
  const id = dbOverviewTabKey({ extensionId, connId, dbName });
  const state0 = useTabsStore.getState();
  // Leaving a table tab with staged edits asks first, like opening a table does.
  if (
    id !== state0.activeDbManagerTabId &&
    guardDirtyDbTab(() => openDbOverviewTab({ extensionId, connId, engine, dbName, label, connLabel, mode }))
  ) {
    return null;
  }
  const overviewMode = mode === 'diagram' ? 'diagram' : 'objects';
  const exists = useTabsStore.getState().dbManagerTabs.some((t) => t.id === id);
  if (!exists) {
    setDbManagerTabs((prev) => [
      ...prev,
      {
        id,
        type: 'db-manager-table',
        view: OVERVIEW_VIEW,
        extensionId,
        connId,
        engine,
        dbName: dbName ?? null,
        tableName: null,
        label: label || dbName || 'Database',
        connLabel: connLabel || undefined,
        overviewMode,
      },
    ]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'db-manager-table', id }));
  } else if (mode) {
    // "Show diagram" on a database whose overview is already open switches it.
    setDbManagerTabs((prev) => prev.map((t) => (t.id === id ? { ...t, overviewMode } : t)));
  }
  setActiveDbManagerTabId(id);
  setActiveTabType('db-manager-table');
  return id;
}

/** Remember per tab what the overview was showing (section, mode). */
export function patchDbOverviewTab(id, patch) {
  setDbManagerTabs((prev) => {
    const target = prev.find((t) => t.id === id);
    if (!target) return prev;
    if (Object.keys(patch).every((k) => target[k] === patch[k])) return prev;
    return prev.map((t) => (t.id === id ? { ...t, ...patch } : t));
  });
}
